/**
 * Cloudinary Image Proxy Utility
 *
 * This utility provides functions to proxy Cloudinary images through our API
 * to avoid CORS issues when loading images directly from Cloudinary.
 */

/**
 * Get a proxied Cloudinary URL that goes through our API
 * @param {string} cloudinaryUrl - The original Cloudinary URL
 * @returns {string} - The proxied URL that goes through our API
 */
export const getProxiedCloudinaryUrl = (cloudinaryUrl) => {
  if (!cloudinaryUrl) {
    return cloudinaryUrl;
  }

  // Skip proxy for local images, data URLs, or blob URLs
  if (cloudinaryUrl.startsWith('../') ||
      cloudinaryUrl.startsWith('./') ||
      cloudinaryUrl.startsWith('data:') ||
      cloudinaryUrl.startsWith('blob:') ||
      cloudinaryUrl.startsWith('http://localhost') ||
      cloudinaryUrl.startsWith('https://localhost')) {
    return cloudinaryUrl;
  }

  // Only proxy actual Cloudinary URLs
  if (!cloudinaryUrl.includes('cloudinary.com') && !cloudinaryUrl.includes('res.cloudinary.com')) {
    return cloudinaryUrl;
  }

  // Encode the Cloudinary URL and create proxy URL
  const encodedUrl = encodeURIComponent(cloudinaryUrl);
  return `${window.location.origin}/api/cloudinary-proxy?url=${encodedUrl}`;
};

/**
 * Get multiple proxied Cloudinary URLs
 * @param {string[]} cloudinaryUrls - Array of Cloudinary URLs
 * @returns {string[]} - Array of proxied URLs
 */
export const getProxiedCloudinaryUrls = (cloudinaryUrls) => {
  if (!Array.isArray(cloudinaryUrls)) {
    return [];
  }

  return cloudinaryUrls.map(url => getProxiedCloudinaryUrl(url));
};

/**
 * Check if a URL is a Cloudinary URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's a Cloudinary URL
 */
export const isCloudinaryUrl = (url) => {
  return url && (url.includes('cloudinary.com') || url.includes('res.cloudinary.com'));
};

/**
 * Extract public ID from Cloudinary URL
 * @param {string} cloudinaryUrl - The Cloudinary URL
 * @returns {string|null} - The public ID or null if not found
 */
export const extractCloudinaryPublicId = (cloudinaryUrl) => {
  if (!isCloudinaryUrl(cloudinaryUrl)) {
    return null;
  }

  try {
    const url = new URL(cloudinaryUrl);
    const pathParts = url.pathname.split('/');
    // Find the part after 'upload' that doesn't start with 'v'
    const uploadIndex = pathParts.findIndex(part => part === 'upload');
    if (uploadIndex !== -1 && uploadIndex + 2 < pathParts.length) {
      // Skip version number and get the public ID (without extension)
      const publicIdWithExt = pathParts[uploadIndex + 2];
      return publicIdWithExt.split('.')[0];
    }
  } catch (error) {
    console.warn('Error extracting Cloudinary public ID:', error);
  }

  return null;
};

/**
 * Process any string that might contain Cloudinary URLs and replace them with proxied versions
 * @param {string} text - The text that might contain Cloudinary URLs
 * @returns {string} - The text with Cloudinary URLs replaced with proxied versions
 */
export const processCloudinaryUrlsInText = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Regular expression to match Cloudinary URLs
  const cloudinaryUrlRegex = /https?:\/\/res\.cloudinary\.com\/[^\/\s]+\/image\/upload\/[^\/\s]+\/[^\/\s]+\.(jpg|jpeg|png|gif|webp|svg)/gi;

  return text.replace(cloudinaryUrlRegex, (match) => {
    return getProxiedCloudinaryUrl(match);
  });
};

/**
 * Process an object recursively to replace any Cloudinary URLs with proxied versions
 * @param {any} obj - The object to process
 * @returns {any} - The object with Cloudinary URLs replaced
 */
export const processObjectForCloudinaryUrls = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => processObjectForCloudinaryUrls(item));
  }

  const processed = { ...obj };

  for (const key in processed) {
    if (typeof processed[key] === 'string') {
      // Check if this looks like an image URL field
      if (key.toLowerCase().includes('image') || key.toLowerCase().includes('url') || key.toLowerCase().includes('photo')) {
        processed[key] = getProxiedCloudinaryUrl(processed[key]);
      } else {
        // Process any Cloudinary URLs in the text
        processed[key] = processCloudinaryUrlsInText(processed[key]);
      }
    } else if (typeof processed[key] === 'object') {
      processed[key] = processObjectForCloudinaryUrls(processed[key]);
    }
  }

  return processed;
};

export default {
  getProxiedCloudinaryUrl,
  getProxiedCloudinaryUrls,
  isCloudinaryUrl,
  extractCloudinaryPublicId,
  processCloudinaryUrlsInText,
  processObjectForCloudinaryUrls
};